import React, { createContext, useContext, useEffect, useRef } from 'react';
import { Card, Skeleton } from 'antd';
import { LineChart, Line, XAxis, Tooltip, ResponsiveContainer } from 'recharts';

/**
 * What every widget can reach on the dashboard: the record drawer (`openDetail` for a tile whose
 * records come from a paged query, `openRows` for rows it already has), the request pop-ups, and
 * `ticks` - counters bumped when something changed (a duty fulfilled, a live request signal) so
 * the widgets showing it read again.
 */
export const DashboardContext = createContext({ ticks: {}, openDetail: () => {}, openRows: () => {}, openRequests: () => {}, bump: () => {} });
export const useDashboard = () => useContext(DashboardContext);

// re-runs `refetch` when one of the named ticks moves (not on the first render)
export const useTicks = (keys, refetch) => {
  const { ticks } = useDashboard();
  const value = keys.map((k) => ticks?.[k] || 0).join(':');
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    refetch?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
};

// small "nothing to plot" placeholder for the sparklines, so an empty period says so
export const SparkEmpty = ({ height = 50 }) => (
  <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bbb', fontSize: 11 }}>
    No data for this period
  </div>
);

export const Spark = ({ data, name, suffix = '' }) => (data?.length > 0 ? (
  <div className="dw-spark">
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={data}>
        <XAxis dataKey="label" hide />
        <Line type="monotone" dataKey="value" stroke="#17a2b8" strokeWidth={2} dot={false} />
        <Tooltip formatter={(value) => [`${value}${suffix}`, name]} labelFormatter={(label) => `Date: ${label}`} />
      </LineChart>
    </ResponsiveContainer>
  </div>
) : <SparkEmpty />);

/** One number with its label (and optional period badge, sub line or sparkline) - the dashboard's metric tile */
export const MetricTile = ({ label, value, badge, alert = false, onClick, hint = 'View details', loading = false, sub, children }) => (
  <Card
    className={`metric-card dw-card${onClick ? ' tile-clickable' : ''}${alert ? ' alert-card' : ''}`}
    size="small"
    onClick={onClick}
    data-hint={onClick ? hint : undefined}
  >
    <div className="metric-header">
      <span className="metric-label">{label}</span>
      {badge}
    </div>
    {loading && value === undefined ? <Skeleton active paragraph={false} title={{ width: '40%' }} /> : <div className="metric-value" data-metric-value>{value}</div>}
    {sub && <div className="dw-sub">{sub}</div>}
    {children}
  </Card>
);

/** A titled panel for charts and lists; the body fills the widget's height */
export const PanelCard = ({ title, extra, children, className = '' }) => (
  <Card className={`chart-card dw-card dw-panel ${className}`} size="small">
    <div className="chart-header">
      <span className="chart-title">{title}</span>
      {extra}
    </div>
    <div className="dw-panel-body">{children}</div>
  </Card>
);
